import { forwardRef, memo, useMemo, type ReactNode } from 'react';
// import clsx from 'clsx';
import { useIntl } from 'react-intl';
// Material UI imports
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import MuiAppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
// Material Icon imports
import TextIcon from 'mdi-material-ui/Text';
// local imports
import { BreadcrumbItem } from '../types';
import AppBarBrand from '../elements/AppBarBrand';
import Breadcrumbs from '../elements/Breadcrumbs';
// SCSS imports
import { toolbar, emptyToolbar } from './AppBar.module.scss';

export const APPBAR_NONE = 'none' as const;
export const APPBAR_EMPTY = 'empty' as const;
export const APPBAR_FULL = 'full' as const;

export type AppBarVariant = typeof APPBAR_NONE | typeof APPBAR_EMPTY | typeof APPBAR_FULL;

type AppBarProps = {
  readonly children?: ReactNode;
  readonly banners?: ReactNode;
  readonly tickers?: ReactNode;
  readonly path?: string;
  readonly breadcrumbs?: BreadcrumbItem[];
  readonly drawerOpen?: boolean;
  readonly toggleDrawer: () => void;
  readonly variant?: AppBarVariant;
  readonly logo: ReactNode;
};

const AppBar = forwardRef<HTMLDivElement, AppBarProps>(({
  children,
  banners,
  tickers,
  path,
  breadcrumbs,
  drawerOpen = false,
  toggleDrawer,
  variant = 'full',
  logo
}, ref) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { formatMessage } = useIntl();
  const label = useMemo(
    () => formatMessage({ id: drawerOpen ? 'header.close_drawer' : 'header.open_drawer' }),
    [formatMessage, drawerOpen]
  );

  return (
    <MuiAppBar
        ref={ref}
        color="inherit"
        elevation={3}
        position={isMobile ? 'relative' : 'fixed'}
    >
      {banners}
      <Toolbar className={variant === APPBAR_EMPTY ? emptyToolbar : toolbar}>
        {(variant === APPBAR_EMPTY && (
          <>
            <AppBarBrand logo={logo}/>
            <Breadcrumbs breadcrumbs={breadcrumbs}/>
            {children}
          </>
        )) || (
          <>
            <IconButton aria-label={label} onClick={toggleDrawer} color="primary" size="large">
              <TextIcon fontSize="large"/>
            </IconButton>
            <AppBarBrand link={path} logo={logo}/>
            <Breadcrumbs breadcrumbs={breadcrumbs}/>
            {children}
          </>
        )}
      </Toolbar>
      {tickers}
    </MuiAppBar>
  );
});

AppBar.displayName = 'AppBar';

export default memo(AppBar);
